if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.setButtonWidth();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.options = this.querySelectorAll('.custom-select__option');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = 'swatch' in this.options[this.options.length - 1].dataset;
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }

    /**
     * Returns the next visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.style.display !== 'none') return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    /**
     * Returns the previous visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.style.display !== 'none') return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    /**
     * Adds event listeners when the options list is open.
     */
    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);
      this.scrollHandler = this.handleScroll.bind(this);

      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
      
      // Close dropdown on scroll when using fixed positioning
      const slider = this.closest('.slider');
      if (slider) {
        slider.addEventListener('scroll', this.scrollHandler);
        this.scrollTarget = slider;
      }
      window.addEventListener('scroll', this.scrollHandler, true);
    }

    /**
     * Removes event listeners added when the options list was open.
     */
    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
      
      if (this.scrollTarget) {
        this.scrollTarget.removeEventListener('scroll', this.scrollHandler);
        this.scrollTarget = null;
      }
      window.removeEventListener('scroll', this.scrollHandler, true);
    }

    /**
     * Handles 'scroll' events - closes dropdown when scrolling outside the listbox.
     * @param {object} evt - Event object.
     */
    handleScroll(evt) {
      // Don't close if scrolling inside the listbox itself
      if (evt.target === this.listbox || this.listbox.contains(evt.target)) {
        return;
      }
      
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    /**
     * Handles 'keydown' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    /**
     * Handles 'mousedown' events on the button element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }

    /**
     * Handles 'mouseover' events on the options list.
     * @param {object} evt - Event object.
     */
    handleMouseover(evt) {
      const option = evt.target.closest('.custom-select__option');
      if (option && this.listbox.contains(option) && !option.classList.contains('is-disabled')) {
        this.focusOption(option);
      }
    }

    /**
     * Handles 'mouseleave' events on the options list.
     */
    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }

    /**
     * Handles 'click' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // mouse down + move + up will trigger a click event on parent - only respond to clicks on LI
      const option = evt.target.closest('.custom-select__option');
      if (option && this.listbox.contains(option) && !option.classList.contains('is-disabled')) {
        this.selectOption(option);
      }
    }

    /**
     * Handles 'blur' events on the options list.
     */
    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    /**
     * Handles 'keydown' events on the options list.
     * @param {object} evt - Event object.
     */
    handleKeyboardNav(evt) {
      let optionToFocus;

      // Disable tabbing if options list is open (as per native select element).
      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        // Focus an option.
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }

          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus);
          }
          break;

        // Select an option.
        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;

        // Cancel and close the options list.
        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;

        // Search for an option and focus the first match (if one exists).
        default:
          optionToFocus = this.findOption(evt.key);

          if (optionToFocus) {
            this.focusOption(optionToFocus);
          }
          break;
      }
    }

    /**
     * Sets the button width to the same as the longest option, to prevent
     * the button width from changing depending on the option selected.
     */
    setButtonWidth() {
      // Get the width of an element without side padding.
      const getHorizontalPadding = (el) => {
        const elStyle = getComputedStyle(el);
        return parseFloat(elStyle.paddingLeft) + parseFloat(elStyle.paddingRight);
      };

      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(this.selectedOption);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);

      this.button.style.setProperty('--custom-select-button-width', `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
    }

    /**
     * Shows the options list.
     */
    showListbox() {
      this.listbox.hidden = false;
      this.listboxOpen = true;

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');

      // Position the listbox using fixed positioning if inside a slider/carousel
      this.positionListboxFixed();

      // Slight delay required to prevent blur event being fired immediately.
      setTimeout(() => {
        this.focusOption(this.selectedOption);
        this.listbox.focus();

        this.addListboxOpenListeners();
      });
    }

    /**
     * Positions the listbox using fixed positioning when inside a slider/carousel
     * to escape overflow clipping.
     */
    positionListboxFixed() {
      // Check if we're inside a slider or carousel
      const isInSlider = this.closest('.slider') || this.closest('.carousel') || this.closest('carousel-slider');
      if (!isInSlider) return;

      // Get the variant selector container for proper width reference
      const variantSelector = this.closest('.card__variant-selector');
      const buttonRect = this.button.getBoundingClientRect();
      
      // Use variant selector width if available, otherwise use button width
      const selectorWidth = variantSelector ? variantSelector.getBoundingClientRect().width : buttonRect.width;
      // Use smaller max-height on mobile (75px), larger on desktop (110px)
      const isMobile = window.innerWidth <= 767;
      const maxListboxHeight = isMobile ? 75 : 110;
      const listboxHeight = Math.min(this.listbox.scrollHeight, maxListboxHeight);
      const viewportHeight = window.innerHeight;
      
      // Set width to match the variant selector
      this.listbox.style.setProperty('--listbox-width', `${selectorWidth}px`);
      this.listbox.style.width = `${selectorWidth}px`;
      
      // Position below the button, aligned with variant selector
      let top = buttonRect.bottom + 4;
      let left = variantSelector ? variantSelector.getBoundingClientRect().left : buttonRect.left;
      
      // If dropdown would go below viewport, position it above the button instead
      if (top + listboxHeight > viewportHeight - 10) {
        top = buttonRect.top - listboxHeight - 4;
      }
      
      this.listbox.style.position = 'fixed';
      this.listbox.style.top = `${top}px`;
      this.listbox.style.left = `${left}px`;
    }

    /**
     * Hides the options list.
     */
    hideListbox() {
      if (!this.listboxOpen) return;

      this.listbox.hidden = true;
      this.listboxOpen = false;

      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');

      // Reset fixed positioning styles
      this.resetListboxPosition();

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }

      this.button.focus();
      this.removeListboxOpenListeners();
    }

    /**
     * Resets the listbox position styles after closing.
     */
    resetListboxPosition() {
      this.listbox.style.position = '';
      this.listbox.style.top = '';
      this.listbox.style.left = '';
      this.listbox.style.width = '';
    }

    /**
     * Finds a matching option from a typed string.
     * @param {string} key - Key pressed.
     * @returns {?Element}
     */
    findOption(key) {
      this.searchString += key;

      // If there's a timer already running, clear it.
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // Wait 500ms to see if another key is pressed, if not then clear the search string.
      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);

      // Find an option that contains the search string (if there is one).
      const matchingOption = Array.from(this.options).find((option) => {
        const label = option.innerText.toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });

      return matchingOption;
    }

    /**
     * Focuses an option.
     * @param {Element} option - <li> element of the option to focus.
     */
    focusOption(option) {
      // Remove focus on currently focused option (if there is one).
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }

      // Set focus on the option.
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      // If option is out of view, scroll the list.
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    /**
     * Selects an option.
     * @param {Element} option - Option <li> element.
     */
    selectOption(option) {
      if (option.classList.contains('is-disabled')) {
        return;
      }
      if (option !== this.selectedOption) {
        // Switch aria-selected attribute to selected option.
        // First, clear any existing selected states in case markup has been replaced.
        this.querySelectorAll('.custom-select__option[aria-selected="true"]').forEach((opt) => {
          if (opt !== option) {
            opt.setAttribute('aria-selected', 'false');
          }
        });
        option.setAttribute('aria-selected', 'true');

        // Update swatch colour in the button.
        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
        }

        // Update the button text and set the option as active.
        this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = document.getElementById(option.id);

        // If this custom select is inside a product card and the option has variant pricing
        // data attributes, update the displayed price for that product card.
        const variantPrice = option.dataset.variantPrice;
        const variantCompareAtPrice = option.dataset.variantCompareAtPrice;
        if (variantPrice) {
          const card = this.closest('product-card, .card--product');
          if (card) {
            card.querySelectorAll('.js-product-card-price .price').forEach((priceEl) => {
              const currentValueEl = priceEl.querySelector('.price__current .js-value');
              if (currentValueEl) {
                currentValueEl.textContent = variantPrice;
              }

              const wasValueEl = priceEl.querySelector('.price__was .js-value');
              if (wasValueEl) {
                wasValueEl.textContent = variantCompareAtPrice || '';
              }

              const hasSale = !!(variantCompareAtPrice && variantCompareAtPrice !== '' && variantCompareAtPrice !== variantPrice);
              priceEl.classList.toggle('price--on-sale', hasSale);
            });
          }
        }

        // If this custom select is inside a product card variant selector, update the card's
        // Add to cart button + quantity selector to use the selected variant id.
        const variantSelector = this.closest('.card__variant-selector');
        if (variantSelector) {
          const card = this.closest('product-card, .card--product, .card');
          const addBtn = card?.querySelector('.js-card-atc-btn');
          const variantsJsonEl = variantSelector.querySelector('.js-card-variants[type="application/json"]');
          const selectedValue = option.dataset.value;
          if (card && addBtn && variantsJsonEl && selectedValue) {
            try {
              const variants = JSON.parse(variantsJsonEl.textContent || '[]');
              // This card-level selector only controls the first option (see product-card.liquid: limit: 1),
              // so choose the first available variant that matches the selected option value.
              const matching = variants.filter((v) => Array.isArray(v.options) && v.options[0] === selectedValue);
              const selectedVariant = matching.find((v) => v.available) || matching[0];
              if (selectedVariant?.id) {
                addBtn.dataset.variantId = `${selectedVariant.id}`;

                // Keep any card quantity selectors in sync (so they listen to the right variant)
                card.querySelectorAll('card-quantity-selector').forEach((qtyEl) => {
                  // Prefer the component API if available so UI state updates immediately.
                  if (typeof qtyEl.setVariantId === 'function') {
                    qtyEl.setVariantId(`${selectedVariant.id}`);
                  } else {
                    qtyEl.dataset.variantId = `${selectedVariant.id}`;
                    // If the element has already been connected, keep its internal state aligned.
                    if ('variantId' in qtyEl) qtyEl.variantId = `${selectedVariant.id}`;
                  }
                });
              }
            } catch (e) {
              // If parsing fails, do nothing - fallback is current_variant.id
            }
          }
        }

        // If a native <select> exists, update its selected value and dispatch a 'change' event.
        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // Dispatch a 'change' event on the custom select element.
          const detail = { selectedValue: option.dataset.value };
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail }));
        }
      }

      this.hideListbox();
    }
  }

  customElements.define('custom-select', CustomSelect);
}